import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUsers } from '../services/userService';
import { useAuth } from './AuthContext';
import { useCacheInvalidation } from './CacheInvalidationContext';

const UserContext = createContext();

export const useUsers = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUsers must be used within a UserProvider');
    }
    return context;
};

export const UserProvider = ({ children }) => {
    const { center, role } = useAuth();
    const { registerRefresh } = useCacheInvalidation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Tutors and Parents don't need the full users list
    const shouldFetchUsers = center && role !== 'TU' && role !== 'PA';

    const fetchUsers = useCallback(async () => {
        if (!shouldFetchUsers) {
            setUsers([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const data = await getUsers();
            setUsers(data);
        } catch (err) {
            setError(err);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, [center, shouldFetchUsers]);

    // Fetch users when center or role changes (skip for tutors)
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers, shouldFetchUsers]);

    // Poll for updates every 10 minutes (when page is visible)
    useEffect(() => {
        const pollInterval = setInterval(() => {
            if (document.visibilityState === 'visible') {
                fetchUsers();
            }
        }, 10 * 60 * 1000); // 10 minutes

        return () => clearInterval(pollInterval);
    }, [fetchUsers]);

    // Refresh function to be called after CRUD operations
    const refreshUsers = useCallback(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Register refresh function for cache invalidation
    useEffect(() => {
        const cleanup = registerRefresh('users', refreshUsers);
        return cleanup;
    }, [registerRefresh, refreshUsers]);

    const value = {
        users,
        loading,
        error,
        refreshUsers,
        fetchUsers
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};

export default UserContext;
